import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";

const HOURS = ["8a", "9a", "10a", "11a", "12p", "1p", "2p", "3p", "4p", "5p", "6p"];

function buildData(heavy) {
  // Conventional: steady drain then recharge. ChargeRevo: oscillates 65-95 snapping up at surface moments.
  const drain = heavy ? 9 : 6;
  let conv = 100;
  let revo = 82;
  const surfaceMoments = [1, 4, 7, 9];
  return HOURS.map((h, i) => {
    conv = Math.max(15, conv - drain - (heavy ? 2 : 0));
    if (i === 8 && conv < 25) conv = Math.min(100, conv + 40); // plug-in recharge
    if (surfaceMoments.includes(i)) revo = Math.min(95, revo + 11);
    else revo = Math.max(65, revo - (heavy ? 6 : 4));
    return { hour: h, conventional: Math.round(conv), revo: Math.round(revo), surface: surfaceMoments.includes(i) };
  });
}

export default function DayOnBatteryChart() {
  const [heavy, setHeavy] = useState(false);
  const data = useMemo(() => buildData(heavy), [heavy]);
  const surfacePts = data.filter((d) => d.surface);

  return (
    <div className="rounded-3xl border border-white/10 bg-navy-deep p-6 md:p-10" data-testid="poc-day-on-battery">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-revo-green">Interactive · A day on battery</p>
          <h3 className="mt-2 font-display text-3xl font-extrabold text-white">Same physics. You just never feel it.</h3>
        </div>
        <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1" data-testid="day-toggle">
          {[["Light day", false], ["Heavy day", true]].map(([label, val]) => (
            <button
              key={label}
              onClick={() => setHeavy(val)}
              data-testid={`day-toggle-${val ? "heavy" : "light"}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                heavy === val ? "bg-revo-green text-navy" : "text-revo-ice hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[340px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(199,212,238,0.12)" vertical={false} />
            <XAxis dataKey="hour" stroke="#C7D4EE" tick={{ fontSize: 12 }} tickLine={false} axisLine={{ stroke: "rgba(199,212,238,0.2)" }} />
            <YAxis domain={[0, 100]} stroke="#C7D4EE" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} unit="%" />
            <Tooltip
              contentStyle={{ background: "#0A1325", border: "1px solid rgba(47,213,118,0.4)", borderRadius: 12, color: "#fff" }}
              labelStyle={{ color: "#C7D4EE" }}
              formatter={(v, n) => [`${v}%`, n === "revo" ? "ChargeRevo" : "Conventional"]}
            />
            <Line type="monotone" dataKey="conventional" stroke="#7C8BB0" strokeWidth={2.5} dot={false} name="conventional" />
            <Line type="monotone" dataKey="revo" stroke="#2FD576" strokeWidth={3.5} dot={false} name="revo" />
            {surfacePts.map((p, i) => (
              <ReferenceDot key={i} x={p.hour} y={p.revo} r={5} fill="#2FD576" stroke="#0A1325" strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 flex flex-wrap gap-6 text-sm text-revo-ice">
        <span className="flex items-center gap-2"><span className="h-0.5 w-6 bg-[#7C8BB0]" /> Conventional laptop</span>
        <span className="flex items-center gap-2"><span className="h-1 w-6 rounded-full bg-revo-green" /> ChargeRevo laptop</span>
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-revo-green" /> Surface moment</span>
      </div>
    </div>
  );
}
